import type { ReactNode } from 'react';
import type { ChecklistCounts, ChecklistFilter } from '../lib/checklist-status';
import { cn } from '../lib/utils';
import { Icon } from './icons';

/** The counts the summary shows — the server's buckets added up
 * ({@link ChecklistCounts}); nothing here counts anything itself. */
export type ChecklistSummaryCounts = Pick<
  ChecklistCounts,
  'total' | 'resolved' | 'covered' | 'notApplicable' | 'waitingOnReview' | 'missing'
>;

/** Who is reading: the "doesn't apply" figure names who signed it. */
export type ChecklistSummaryAudience = 'intermediary' | 'seller';

/** Props a figure rendered as a link receives (spread them onto the link). */
export interface ChecklistFigureLinkProps {
  className: string;
  'aria-label': string;
  children: ReactNode;
}

interface Figure {
  filter: ChecklistFilter;
  value: ReactNode;
  label: string;
  /** Screen-reader sentence for the figure's action. */
  describe: string;
  accent?: boolean;
}

function figuresFor(
  counts: ChecklistSummaryCounts,
  audience: ChecklistSummaryAudience,
): Figure[] {
  const items = (n: number) => `${n} ${n === 1 ? 'item' : 'items'}`;
  // What needs action leads: still missing, then (only while there are any) the AI
  // matches waiting on a person. "All" is its own figure, so no figure that names
  // a subset ever shows the whole list (2026-10-30: "Resolved" used to mean All).
  const figures: Figure[] = [
    {
      filter: 'missing',
      value: counts.missing,
      label: 'Still missing',
      describe: `Still missing: ${items(counts.missing)}. Show them.`,
      accent: true,
    },
  ];
  if (counts.waitingOnReview > 0) {
    figures.push({
      filter: 'waiting',
      value: counts.waitingOnReview,
      label: 'Waiting on review',
      describe: `Waiting on review: ${items(counts.waitingOnReview)}. Show them.`,
    });
  }
  figures.push(
    {
      filter: 'covered',
      value: counts.covered,
      label: 'Covered by a document',
      describe: `Covered by a document: ${items(counts.covered)}. Show them.`,
    },
    {
      filter: 'na',
      value: counts.notApplicable,
      label:
        audience === 'seller' ? 'You signed “doesn’t apply”' : 'Seller signed “doesn’t apply”',
      describe: `${
        audience === 'seller' ? 'You signed' : 'Seller signed'
      } doesn’t apply: ${items(counts.notApplicable)}. Show them.`,
    },
    {
      filter: 'all',
      value: counts.total,
      label: 'All items',
      describe: `All items: ${items(counts.total)}. Show them all.`,
    },
  );
  return figures;
}

export interface ChecklistSummaryProps {
  counts: ChecklistSummaryCounts;
  audience?: ChecklistSummaryAudience;
  /** Optional heading over the figures (e.g. the room or the phases covered). */
  title?: ReactNode;
  /** `compact` — the rooms dashboard's smaller per-room card. */
  size?: 'full' | 'compact';
  /** The filter currently applied (that figure reads as selected). */
  active?: ChecklistFilter | null;
  /** Figures become buttons that apply their filter in place. */
  onSelect?: (filter: ChecklistFilter) => void;
  /** Figures become links (e.g. a router Link to the filtered checklist). Wins over onSelect. */
  renderLink?: (filter: ChecklistFilter, props: ChecklistFigureLinkProps) => ReactNode;
  className?: string;
}

/**
 * The checklist at a glance (owner ask 2026-10-07, after the intermediaries page
 * stat card): big figures with small labels — still missing (in the accent
 * colour), waiting on review (only while there are any), covered by a document,
 * signed "doesn't apply" and all items — over "x of y resolved" and a slim
 * progress bar. Every figure names exactly the items it shows (2026-10-30). Each
 * figure applies its checklist filter, and says so: a "Show:" line above them,
 * a "Show" prompt on every figure and a ticked "Showing" on the one in force
 * (owner ask 2026-10-22: the figures were the filter but did not look pressable).
 * Facts only: the counts it is handed, no score and no counting of its own.
 */
export function ChecklistSummary({
  counts,
  audience = 'intermediary',
  title,
  size = 'full',
  active = null,
  onSelect,
  renderLink,
  className,
}: ChecklistSummaryProps) {
  const compact = size === 'compact';
  const figures = figuresFor(counts, audience);
  const percent = counts.total > 0 ? Math.round((counts.resolved / counts.total) * 100) : 0;
  const interactive = Boolean(renderLink || onSelect);
  // The explicit "this is a control" cues are for the full-size card; the compact
  // per-room card on the dashboard is a row of links and stays quiet.
  const cues = interactive && !compact;

  return (
    <section
      aria-label={typeof title === 'string' ? title : 'Checklist progress'}
      className={cn('space-y-3', className)}
    >
      {title ? (
        <h3
          className={cn(
            'font-semibold text-ink',
            compact ? 'text-[13px]' : 'text-sm md:text-[15px]',
          )}
        >
          {title}
        </h3>
      ) : null}
      {cues ? (
        <p className="text-xs font-medium text-ink-2">
          <span className="font-semibold text-ink">Show:</span> choose a figure to list only those
          items below.
        </p>
      ) : null}
      <ul
        className={cn(
          'grid gap-2',
          figures.length === 5 ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4',
        )}
      >
        {figures.map((f, i) => {
          const selected = active === f.filter;
          const tile = cn(
            'flex h-full w-full flex-col items-start rounded-md border text-left transition-colors',
            compact ? 'gap-0.5 px-3 py-2' : 'gap-1 px-3 py-3 md:px-4',
            interactive &&
              'cursor-pointer hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            cues && !selected && 'shadow-sm hover:border-primary',
            selected ? 'border-primary bg-primary-soft ring-1 ring-primary' : 'border-line bg-surface',
          );
          const body = (
            <>
              <span
                className={cn(
                  'font-semibold leading-none tabular-nums tracking-tight',
                  compact ? 'text-xl' : 'text-3xl md:text-[34px]',
                  f.accent ? 'text-brass' : 'text-ink',
                )}
              >
                {f.value}
              </span>
              <span
                className={cn(
                  'leading-snug',
                  compact ? 'text-[11px]' : 'text-xs',
                  f.accent ? 'font-medium text-brass' : 'text-ink-2',
                )}
              >
                {f.label}
              </span>
              {cues ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold',
                    selected ? 'text-primary' : 'text-ink-3 underline underline-offset-2',
                  )}
                >
                  {selected ? <Icon name="check" size={12} /> : null}
                  {selected ? 'Showing' : 'Show'}
                </span>
              ) : null}
            </>
          );
          // The fifth figure spans the row on a phone so the 2×2 grid stays whole.
          const span = figures.length === 5 && i === 4 ? 'col-span-2 sm:col-span-1' : undefined;
          return (
            <li key={f.filter} className={span}>
              {renderLink ? (
                renderLink(f.filter, { className: tile, 'aria-label': f.describe, children: body })
              ) : onSelect ? (
                <button
                  type="button"
                  className={tile}
                  aria-label={f.describe}
                  aria-pressed={selected}
                  onClick={() => onSelect(f.filter)}
                >
                  {body}
                </button>
              ) : (
                <div className={tile}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="space-y-1">
        <p className={cn('text-ink-2', compact ? 'text-[11px]' : 'text-xs')}>
          <span className="font-semibold tabular-nums text-ink">{counts.resolved}</span> of{' '}
          <span className="tabular-nums">{counts.total}</span> resolved
        </p>
      <div
        className={cn('w-full overflow-hidden rounded-full bg-muted', compact ? 'h-1' : 'h-1.5')}
        role="progressbar"
        aria-label={`${counts.resolved} of ${counts.total} items resolved`}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
      </div>
      </div>
    </section>
  );
}
